<script lang="ts">
  // Daily login-streak reward, shown once per day on login. The reward is already granted
  // server-side (see rewards.ts); this is the celebratory collect screen. Most days pay DP;
  // day DAILY_GOLD_DAY awards the one-time Beta Gold Duel Disk (carried in `cosmetic`). The
  // 7-day track doubles as the upcoming-rewards preview so players can see the disk coming.
  // Keep the streak alive by logging in daily; miss a day and it resets to Day 1.
  import { fade, scale, fly } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import { dailyRewardKind, dailyDp, type CosmeticKind } from "@ygoevo/world-protocol";
  import DataWindow from "./ui/DataWindow.svelte";
  import DuelDiskSwatch from "./duel/DuelDiskSwatch.svelte";
  import { createHotkey, onEscape } from "../hotkeys.ts";
  import { draggableDialog } from "../actions/draggableDialog.ts";

  let {
    streak,
    dp,
    cosmetic,
    gender = "cm",
    onClose,
  }: {
    streak: number;
    dp: number;
    cosmetic?: { kind: CosmeticKind; itemId: string };
    gender?: "cm" | "cw";
    onClose: () => void;
  } = $props();

  // Today's reward is the Gold Duel Disk when the server sent a cosmetic (day DAILY_GOLD_DAY,
  // first time only). The gold Battle Disk is variant 03 / colour 2 (see BETA_GOLD_DUELDISK_SKU).
  const isGoldDisk = $derived(!!cosmetic);

  // Upcoming-rewards preview: the first 7 streak days, each showing its reward (DP or the disk).
  // A day is "claimed" once the streak reaches it; the current day is highlighted.
  const TRACK = 7;
  const days = $derived(
    Array.from({ length: TRACK }, (_, i) => {
      const day = i + 1;
      return { day, kind: dailyRewardKind(day), dp: dailyDp(day), claimed: day <= streak, current: day === streak };
    }),
  );

  // Escape or Enter collects the reward.
  onEscape(() => onClose());
  createHotkey("Enter", () => onClose(), { conflictBehavior: "allow" });
</script>

<div class="mobile-dialog-host fixed inset-0 z-[60] grid place-items-center p-4" role="presentation">
  <button class="modal-scrim absolute inset-0 bg-void/52" transition:fade={{ duration: 200 }} onclick={onClose} aria-label="Collect"></button>

  <div
    class="mobile-full-dialog relative w-[min(460px,92vw)]"
    role="dialog"
    aria-modal="true"
    aria-label="Daily reward"
    use:draggableDialog
    transition:scale={{ start: 0.94, opacity: 0, duration: 260, easing: expoOut }}
  >
    <DataWindow c={18} fill class="mobile-dialog-panel">
      <div data-dialog-drag-handle class="dialog-drag-handle shrink-0 px-6 pt-6 text-center phone:px-4 phone:pt-3">
        <div class="font-display text-xs uppercase tracking-[0.24em] text-cyan">Daily Reward</div>
        <div class="mt-2 flex items-center justify-center gap-2 phone:mt-0.5">
          <span class="text-2xl phone:text-lg">🔥</span>
          <span class="font-display text-3xl font-bold text-ink phone:text-xl">Day {streak}</span>
        </div>
        <p class="mt-1 text-sm text-ink-dim phone:text-xs">{streak === 1 ? "Welcome back, a fresh streak begins!" : "Streak going strong, keep it up!"}</p>
      </div>

      <!-- track + payout share ONE scroll region so the Collect button stays pinned -->
      <div class="mobile-dialog-scroll max-h-[58dvh] min-h-0 flex-1 overflow-y-auto scroll-cyber">
      <!-- 7-day reward track, also the upcoming-rewards preview (DP coins + the Gold Disk on day 2) -->
      <div class="flex justify-center gap-1.5 px-5 pt-5 phone:px-3 phone:pt-3">
        {#each days as d (d.day)}
          <div class="flex flex-1 flex-col items-center gap-1">
            <div
              class="grid h-11 w-full place-items-center chamfer-btn transition-colors {d.kind === 'golddisk'
                ? d.claimed
                  ? 'bg-gold/25 shadow-[0_0_14px_rgba(255,179,64,0.5)]'
                  : 'bg-gold/12'
                : d.claimed
                  ? 'bg-cyan/15 text-cyan glow-cyan'
                  : 'bg-void-2/50 text-ink-dim/45'} {d.current ? 'outline outline-1 outline-cyan' : ''}"
              style="--c:5px"
              title={d.kind === 'golddisk' ? 'Gold Duel Disk' : `${d.dp} DP`}
            >
              {#if d.kind === "golddisk"}
                <div class="size-9 {d.claimed ? '' : 'opacity-50'}"><DuelDiskSwatch slot="dd" variant="03" color={2} {gender} /></div>
              {:else}
                <div class="flex flex-col items-center leading-none">
                  <span class="font-display text-[0.78rem] font-bold tabular-nums">{d.dp}</span>
                  <span class="font-display text-[0.5rem] tracking-wide opacity-70">DP</span>
                </div>
              {/if}
            </div>
            <span class="font-display text-[0.55rem] tracking-wide {d.claimed ? (d.kind === 'golddisk' ? 'text-gold' : 'text-cyan') : 'text-ink-dim/45'}">D{d.day}</span>
          </div>
        {/each}
      </div>

      <!-- payout: the gold disk reveal on the gold day, otherwise the DP coins -->
      {#if isGoldDisk}
        <div class="px-6 pt-6 text-center phone:px-3 phone:pt-3">
          <div class="font-display text-[0.7rem] font-semibold uppercase tracking-[0.26em] text-gold" style="text-shadow:0 0 12px color-mix(in oklab,var(--color-gold) 55%,transparent)">Beta Exclusive Reward</div>
          <div class="disk-well chamfer" style="--c:14px" in:fly={{ y: 14, duration: 360, delay: 200, easing: expoOut }}>
            <span class="aura" aria-hidden="true"></span>
            <DuelDiskSwatch slot="dd" variant="03" color={2} {gender} />
          </div>
          <h2 class="mt-3 font-display text-[1.4rem] font-bold leading-tight text-ink phone:mt-1.5 phone:text-[1.05rem]">Beta Gold Duel Disk</h2>
          <p class="mx-auto mt-1.5 max-w-[34ch] text-sm leading-relaxed text-ink-dim phone:text-xs">The <strong class="text-gold">exclusive gold Battle Disk</strong> is yours, equip it any time in the Customizer.</p>
        </div>
      {:else}
        <div class="px-6 py-6 text-center phone:px-3 phone:py-3">
          <div
            class="inline-flex items-baseline gap-2 chamfer-btn bg-gold/15 px-7 py-3 glow-gold phone:px-4 phone:py-2"
            style="--c:9px"
            in:fly={{ y: 12, duration: 320, delay: 200 }}
          >
            <span class="font-display text-3xl font-bold text-gold tabular-nums">+{dp.toLocaleString()}</span>
            <span class="font-display text-sm font-bold uppercase tracking-widest text-gold/80">DP</span>
          </div>
          {#if dp >= 1000}
            <div class="mt-3 font-display text-[0.7rem] uppercase tracking-[0.18em] text-cyan">Max daily bonus!</div>
          {/if}
        </div>
      {/if}
      </div>

      <div class="shrink-0 border-t border-cyan-dim/25 p-3 text-center phone:p-2">
        <button onclick={onClose} class="chamfer-btn bg-cyan/15 px-10 py-2.5 font-display text-sm font-bold uppercase tracking-[0.14em] text-cyan transition-colors hover:glow-cyan" style="--c:8px">
          Collect
        </button>
      </div>
    </DataWindow>
  </div>
</div>

<style>
  /* Golden well that frames the disk on the gold-disk day (mirrors the old GoldenDiskAward). */
  :global(.phone) .disk-well { width: 6.5rem; margin-top: 0.4rem; }
  .disk-well {
    position: relative;
    width: 10rem;
    margin: 1rem auto 0;
    aspect-ratio: 4 / 3;
    display: grid;
    place-items: center;
    overflow: hidden;
    background: radial-gradient(120% 120% at 50% 35%, #1a1606, #0a1020 70%);
    box-shadow:
      inset 0 0 0 1.5px color-mix(in oklab, var(--color-gold) 45%, transparent),
      0 0 22px color-mix(in oklab, var(--color-gold) 22%, transparent);
  }
  .aura {
    position: absolute;
    inset: -30%;
    pointer-events: none;
    background: radial-gradient(circle at 50% 45%, color-mix(in oklab, var(--color-gold) 40%, transparent), transparent 60%);
    animation: aura-pulse 3.2s ease-in-out infinite;
  }
  @keyframes aura-pulse {
    0%, 100% { opacity: 0.55; transform: scale(0.98); }
    50% { opacity: 0.9; transform: scale(1.04); }
  }
  @media (prefers-reduced-motion: reduce) {
    .aura { animation: none; opacity: 0.7; }
  }
</style>
